'use client';

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import ThoughtCard from './ThoughtCard';
import Icon from '@/components/ui/AppIcon';
import { entryService } from '@/services/entryService';
import { supabase } from '@/lib/supabase';
import { stripHtmlTags } from '@/utils/sanitizeHtml';
import { normalizeExcerptSpacing } from '@/utils/excerpt';
import { objectPositionFromCrop } from '@/lib/images/objectPosition';

const formatDateSafe = (value?: string | null) => {
  if (!value) return '';
  const dateStr = value.length === 10 ? `${value}T12:00:00Z` : value;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

interface Thought {
  id: string;
  title: string;
  excerpt: string;
  publishedDate: string;
  mode: string;
  subjects: string[];
  subSubjects?: string[];
  toneGenre?: string[];
  intents?: string[];
  coverImage?: string;
  coverImageAlt?: string;
  coverImageMonochrome?: boolean;
  projectName?: string;
  seriesName?: string;
  objectPosition?: string;
}

interface ThoughtFeedContentProps {
  onFilterToggle?: () => void;
}

const ThoughtFeedContent = ({}: ThoughtFeedContentProps) => {
  const searchParams = useSearchParams();
  const [isHydrated, setIsHydrated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [displayedThoughts, setDisplayedThoughts] = useState<Thought[]>([]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Mock data removed - now fetching from database


  const loadThoughts = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch published thought entries
      const { data: entries, error } = await entryService.getAllPublished('thought');
      
      if (error) {
        console.error('Error loading thoughts:', error);
        setDisplayedThoughts([]);
        setIsLoading(false);
        return;
      }

      if (!entries || entries.length === 0) {
        setDisplayedThoughts([]);
        setIsLoading(false);
        return;
      }

      // Fetch subjects for all entries
      const entryIds = entries.map(e => e.id);
      const { data: subjectsData } = await supabase
        .from('entry_subjects')
        .select('entry_id, subject_id, sub_subject_id')
        .in('entry_id', entryIds);

      // Create maps of entry_id -> subject names / sub-subject names
      const subjectMap = new Map<string, string[]>();
      const subSubjectMap = new Map<string, string[]>();
      if (subjectsData && subjectsData.length > 0) {
        const subjectIds = Array.from(new Set(subjectsData.map(s => s.subject_id).filter(Boolean)));
        const { data: subjectNames } = await supabase
          .from('subjects')
          .select('id, name')
          .in('id', subjectIds);

        const subSubjectIds = Array.from(new Set(subjectsData.map(s => s.sub_subject_id).filter(Boolean)));
        const { data: subSubjectNames } = await supabase
          .from('sub_subjects')
          .select('id, name')
          .in('id', subSubjectIds);

        if (subjectNames) {
          const subjectIdToName = new Map(subjectNames.map(s => [s.id, s.name]));
          const subSubjectIdToName = new Map(subSubjectNames?.map(s => [s.id, s.name]) || []);
          subjectsData.forEach(es => {
            const subjectName = subjectIdToName.get(es.subject_id);
            const subSubjectName = es.sub_subject_id ? subSubjectIdToName.get(es.sub_subject_id) : undefined;
            if (subjectName) {
              const current = subjectMap.get(es.entry_id) || [];
              if (!current.includes(subjectName)) {
                subjectMap.set(es.entry_id, [...current, subjectName]);
              }
            }
            if (subSubjectName) {
              const currentSub = subSubjectMap.get(es.entry_id) || [];
              if (!currentSub.includes(subSubjectName)) {
                subSubjectMap.set(es.entry_id, [...currentSub, subSubjectName]);
              }
            }
          });
        }
      }

      // Fetch tones for entries
      const { data: tonesData } = await supabase
        .from('entry_tones')
        .select('entry_id, tone')
        .in('entry_id', entryIds);

      const toneMap = new Map<string, string[]>();
      if (tonesData) {
        tonesData.forEach(et => {
          const current = toneMap.get(et.entry_id) || [];
          toneMap.set(et.entry_id, [...current, et.tone]);
        });
      }

      // Fetch intents for entries
      const { data: intentsData } = await supabase
        .from('entry_intents')
        .select('entry_id, intent')
        .in('entry_id', entryIds);

      const intentMap = new Map<string, string[]>();
      if (intentsData) {
        intentsData.forEach(ei => {
          const current = intentMap.get(ei.entry_id) || [];
          intentMap.set(ei.entry_id, [...current, ei.intent]);
        });
      }

      // Transform entries to Thought format
      const thoughts: Thought[] = entries.map((entry) => {
        const mode = entry.thought_mode || entry.image_mode || 'Note';
        const rawExcerpt =
          entry.excerpt ||
          (entry.body_html ? stripHtmlTags(entry.body_html).substring(0, 150) : '');
        const excerpt =
          rawExcerpt && rawExcerpt.length === 150 ? normalizeExcerptSpacing(rawExcerpt) + '...' : normalizeExcerptSpacing(rawExcerpt);

        return {
          id: entry.id,
          title: entry.title,
          excerpt,
          publishedDate: formatDateSafe(entry.publish_date || entry.publishedAt || entry.createdAt),
          mode: mode.charAt(0).toUpperCase() + mode.slice(1),
          subjects: subjectMap.get(entry.id) || [],
          subSubjects: subSubjectMap.get(entry.id) || [],
          toneGenre: toneMap.get(entry.id)?.map(t => t.charAt(0).toUpperCase() + t.slice(1)) || [],
          intents: intentMap.get(entry.id)?.map(i => i.charAt(0).toUpperCase() + i.slice(1)) || [],
          coverImage: entry.coverImageUrl || undefined,
          coverImageAlt: entry.title,
          coverImageMonochrome: (entry.metadata?.coverImageMonochrome as boolean) || false,
          projectName: undefined, // TODO: Fetch project name
          seriesName: entry.series || undefined,
          objectPosition: objectPositionFromCrop(entry.crops?.['16:9'], { x: entry.focal_x, y: entry.focal_y }),
        };
      });

      // Apply filters if any
      let filteredThoughts = thoughts;
      const subjectFilter = searchParams?.get('subject');
      const subSubjectFilter = searchParams?.get('sub_subject');
      
      if (subjectFilter) {
        filteredThoughts = filteredThoughts.filter(t => 
          t.subjects.some(s => s.toLowerCase().includes(subjectFilter.toLowerCase()))
        );
      }
      
      if (subSubjectFilter) {
        filteredThoughts = filteredThoughts.filter((t) =>
          (t.subSubjects || []).some((s) =>
            s.toLowerCase().includes(subSubjectFilter.toLowerCase())
          )
        );
      }

      // Show all filtered thoughts (no pagination)
      setDisplayedThoughts(filteredThoughts);
    } catch (error) {
      console.error('Error loading thoughts:', error);
      setDisplayedThoughts([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isHydrated) return;
    loadThoughts();
  }, [isHydrated, loadThoughts]);

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:212:6" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="212" data-component-end-line="230" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:213:8" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="213" data-component-end-line="215" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-8%22%7D" className="flex items-center justify-between mb-8">
          <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:214:10" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="214" data-component-end-line="214" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-10%20w-48%20bg-muted%20rounded%20animate-pulse%22%7D" className="h-10 w-48 bg-muted rounded animate-pulse" />
        </div>
        <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:216:8" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="216" data-component-end-line="229" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) =>
            <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:218:12" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="218" data-component-end-line="227" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-lg%20shadow-editorial%20overflow-hidden%22%7D"
              key={i}
              className="bg-card rounded-lg shadow-editorial overflow-hidden">
              <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:221:14" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="221" data-component-end-line="221" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-48%20bg-muted%20animate-pulse%22%7D" className="h-48 bg-muted animate-pulse" />
              <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:222:14" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="222" data-component-end-line="226" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-6%20space-y-4%22%7D" className="p-6 space-y-4">
                <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:223:16" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="223" data-component-end-line="223" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-6%20bg-muted%20rounded%20animate-pulse%22%7D" className="h-6 bg-muted rounded animate-pulse" />
                <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:224:16" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="224" data-component-end-line="224" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-4%20bg-muted%20rounded%20animate-pulse%22%7D" className="h-4 bg-muted rounded animate-pulse" />
                <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:225:16" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="225" data-component-end-line="225" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-4%20bg-muted%20rounded%20animate-pulse%20w-3%2F4%22%7D" className="h-4 bg-muted rounded animate-pulse w-3/4" />
              </div>
            </div>
          )}
        </div>
      </div>
    );

  }

  const activeFilters = Array.from(searchParams.entries()).filter(([, value]) => Boolean(value));

  return (
    <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:238:4" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="238" data-component-end-line="298" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
      <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:239:6" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="239" data-component-end-line="249" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-8%22%7D" className="flex items-center justify-between mb-8">
        <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:240:8" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="240" data-component-end-line="248" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <h1 data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:241:10" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="241" data-component-end-line="243" data-component-file="ThoughtFeedContent.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22font-heading%20font-bold%20text-4xl%20text-foreground%20mb-2%22%2C%22textContent%22%3A%22Writing%22%7D" className="font-heading font-bold text-4xl text-foreground mb-2">
            Writing
          </h1>
          <p data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:244:10" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="244" data-component-end-line="247" data-component-file="ThoughtFeedContent.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%22%7D" className="text-muted-foreground">
            {displayedThoughts.length} {displayedThoughts.length === 1 ? 'piece' : 'pieces'}
            {activeFilters.length > 0 && ' (filtered)'}
          </p>
        </div>
      </div>

        <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:251:8" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="251" data-component-end-line="255" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {displayedThoughts.map((thought) =>
          <ThoughtCard data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:253:10" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="253" data-component-end-line="253" data-component-file="ThoughtFeedContent.tsx" data-component-name="ThoughtCard" data-component-content="%7B%22elementName%22%3A%22ThoughtCard%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" key={thought.id} {...thought} />
          )}
        </div>

        {isLoading &&
        <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:258:8" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="258" data-component-end-line="272" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-6%20mt-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {[1, 2].map((i) =>
          <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:260:10" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="260" data-component-end-line="270" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-lg%20shadow-editorial%20overflow-hidden%22%7D"
            key={i}
            className="bg-card rounded-lg shadow-editorial overflow-hidden">

                <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:264:16" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="264" data-component-end-line="264" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-48%20bg-muted%20animate-pulse%22%7D" className="h-48 bg-muted animate-pulse" />
                <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:265:16" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="265" data-component-end-line="269" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-6%20space-y-4%22%7D" className="p-6 space-y-4">
                  <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:266:18" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="266" data-component-end-line="266" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-6%20bg-muted%20rounded%20animate-pulse%22%7D" className="h-6 bg-muted rounded animate-pulse" />
                  <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:267:18" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="267" data-component-end-line="267" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-4%20bg-muted%20rounded%20animate-pulse%22%7D" className="h-4 bg-muted rounded animate-pulse" />
                  <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:268:18" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="268" data-component-end-line="268" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-4%20bg-muted%20rounded%20animate-pulse%20w-3%2F4%22%7D" className="h-4 bg-muted rounded animate-pulse w-3/4" />
                </div>
              </div>
          )}
          </div>
        }

        {!isLoading && displayedThoughts.length > 0 &&
        <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:276:8" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="276" data-component-end-line="280" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-12%20text-center%22%7D" className="mt-12 text-center">
            <p data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:277:12" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="277" data-component-end-line="279" data-component-file="ThoughtFeedContent.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22caption%20text-muted-foreground%22%2C%22textContent%22%3A%22You've%20reached%20the%20end%20of%20the%20feed%22%7D" className="caption text-muted-foreground">
              You&apos;ve reached the end of the feed
            </p>
          </div>
        }

        {!isLoading && displayedThoughts.length === 0 &&
        <div data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:284:8" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="284" data-component-end-line="296" data-component-file="ThoughtFeedContent.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-12%20text-center%20py-16%22%7D" className="mt-12 text-center py-16">
            <Icon data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:285:12" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="285" data-component-end-line="288" data-component-file="ThoughtFeedContent.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22DocumentTextIcon%22%2C%22className%22%3A%22mx-auto%20mb-4%20text-muted-foreground%22%7D"
            name="DocumentTextIcon"
            size={48}
            className="mx-auto mb-4 text-muted-foreground" />

            <h3 data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:290:12" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="290" data-component-end-line="292" data-component-file="ThoughtFeedContent.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-heading%20font-semibold%20text-xl%20text-foreground%20mb-2%22%2C%22textContent%22%3A%22No%20Writing%20Found%22%7D" className="font-heading font-semibold text-xl text-foreground mb-2">
              No Writing Found
            </h3>
            <p data-component-id="src/app/thoughts-feed/components/ThoughtFeedContent.tsx:293:12" data-component-path="src/app/thoughts-feed/components/ThoughtFeedContent.tsx" data-component-line="293" data-component-end-line="295" data-component-file="ThoughtFeedContent.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%22%2C%22textContent%22%3A%22Try%20adjusting%20your%20filters%20to%20see%20more%20content%22%7D" className="text-muted-foreground">
              Try adjusting your filters to see more content
            </p>
          </div>
        }
    </div>);

};

export default ThoughtFeedContent;